import ClinicalFilters from '../../CustomFilterComponents/ClinicalFilters';
import Email from '../../CustomFilterComponents/EmailFilter';
import Media from '../../CustomFilterComponents/MediaFilter';
import PrescribingBehaviorFilter from '../../CustomFilterComponents/PrescribingBehaviorFilter';
import Search from '../../CustomFilterComponents/SearchFilter';
import Site from '../../CustomFilterComponents/SiteFilter';
import Social from '../../CustomFilterComponents/SocialFilter';
import { TreeIncludeExcludeFilter } from '../../CustomFilterComponents/TreeIncludeExcludeFilter';
import { ProfessionComponent } from '../FilterConfigCustomComponents';
import type { FilterConfigSubGroupItem } from '../FilterConfigTypes';

const IABTreeFilter = (
  <TreeIncludeExcludeFilter
    dictionary="IAB"
    filterName="contextual_iab_taxonomy_id_filter"
    decileFilterName="contextual_iab_decile_filter"
    recencyFilterName="contextual_lookback_period_filter"
  />
);

const MeshTreeFilter = (
  <TreeIncludeExcludeFilter
    dictionary="MeSH"
    filterName="contextual_mesh_taxonomy_id_filter"
    decileFilterName="contextual_mesh_decile_filter"
    recencyFilterName="contextual_lookback_period_filter"
  />
);

export const subGroupItems: Array<FilterConfigSubGroupItem> = [
  {
    groupLabelId: 'iab_subgroup',
    parentGroupLabel: 'Contextual',
    groupLabel: 'IAB',
    ordinalPosition: 10,
    tooltip:
      'Specify the IAB (Interactive Advertising Bureau) Descriptor to refine your audience. This filter allows you to target healthcare professionals based on their digital behavior and interests, as categorized by the IAB, ensuring your outreach is aligned with their online preferences and activities.',
    filtersInGroup: [
      { name: 'contextual_iab_taxonomy_id_filter', index: 1 },
      { name: 'contextual_iab_decile_filter', index: 2 },
    ],
    icon: 'Iab',
    customComponent: IABTreeFilter,
    requiredPermission: 'Moments',
  },
  {
    groupLabelId: 'mesh_subgroup',
    parentGroupLabel: 'Contextual',
    groupLabel: 'MeSH',
    ordinalPosition: 10,
    tooltip:
      'Specify the MesH Descriptor to refine your audience. This filter allows you to target healthcare professionals based on their digital behavior and interests, as categorized by the Mesh, ensuring your outreach is aligned with their online preferences and activities.',
    filtersInGroup: [
      { name: 'contextual_mesh_taxonomy_id_filter', index: 1 },
      { name: 'contextual_mesh_decile_filter', index: 2 },
    ],
    icon: 'Endemics',
    customComponent: MeshTreeFilter,
    requiredPermission: 'Moments',
  },
  {
    groupLabelId: 'prescriptions_subgroup',
    parentGroupLabel: 'Clinical',
    groupLabel: 'Prescriptions',
    ordinalPosition: 1,
    tooltip: 'What particular drugs NPIs were/were not prescribing',
    filtersInGroup: [
      { name: 'prescribed_drug_name_filter', index: 1 },
      { name: 'prescribed_drug_decile_filter', index: 2 },
    ],
    icon: 'Drugcoverage',
    customComponent: (
      <ClinicalFilters
        label="Drugs"
        decilesFilterName="prescribed_drug_decile_filter"
        includeExcludeFilterName="prescribed_drug_name_filter"
        recencyFilterName="claims_lookback_period_filter"
        recencyLabel="Clinical"
      />
    ),
  },
  {
    groupLabelId: 'profession_and_speciality_subgroup',
    parentGroupLabel: 'NPI Attributes',
    groupLabel: 'Profession & Specialty',
    ordinalPosition: 1,
    tooltip:
      'Specify the profession & speciality of doctors to refine your audience. This filter allows you to target healthcare professionals based on their specific medical profession or role.',
    filtersInGroup: [
      { name: 'profession_filter', index: 1 },
      { name: 'specialty_filter', index: 2 },
    ],
    icon: 'Specialties',
    customComponent: ProfessionComponent,
  },
  {
    groupLabelId: 'prescribing_behavior_subgroup',
    parentGroupLabel: 'Clinical',
    groupLabel: 'Prescribing behavior',
    ordinalPosition: 2,
    tooltip: 'How does the prescribing behavior changed over time',
    filtersInGroup: [
      { name: 'prescribing_behavior_lookback_period_filter', index: 1 },
      { name: 'prescribing_behavior_drug_name_filter', index: 2 },
      { name: 'prescribing_behavior_prescription_type_filter', index: 3 },
      { name: 'prescribing_behavior_change_filter', index: 4 },
      { name: 'prescribing_behavior_decile_filter', index: 5 },
    ],
    icon: 'RX',
    customComponent: <PrescribingBehaviorFilter />,
  },
  {
    groupLabelId: 'diagnoses_subgroup',
    parentGroupLabel: 'Clinical',
    groupLabel: 'Diagnoses',
    ordinalPosition: 3,
    tooltip: 'What particular diagnoses NPIs were/were not giving',
    filtersInGroup: [
      { name: 'diagnosis_lookback_period_filter', index: 1 },
      { name: 'diagnosis_name_filter', index: 2 },
      { name: 'diagnosis_decile_filter', index: 3 },
    ],
    icon: 'Diagnosis',
    customComponent: (
      <ClinicalFilters
        label="Diagnosis"
        decilesFilterName="diagnosis_decile_filter"
        includeExcludeFilterName="diagnosis_name_filter"
        recencyFilterName="claims_lookback_period_filter"
        recencyLabel="Clinical"
      />
    ),
  },
  {
    groupLabelId: 'procedures_subgroup',
    parentGroupLabel: 'Clinical',
    groupLabel: 'Procedures',
    ordinalPosition: 4,
    tooltip: 'What particular procedures NPIs were/were not performing',
    filtersInGroup: [
      { name: 'procedure_lookback_period_filter', index: 1 },
      { name: 'procedure_name_filter', index: 2 },
      { name: 'procedure_decile_filter', index: 3 },
    ],
    icon: 'Healthcondition',
    customComponent: (
      <ClinicalFilters
        label="Procedures"
        decilesFilterName="procedure_decile_filter"
        includeExcludeFilterName="procedure_name_filter"
        recencyFilterName="claims_lookback_period_filter"
        recencyLabel="Clinical"
      />
    ),
  },
  {
    groupLabelId: 'site_subgroup',
    parentGroupLabel: 'Engagement',
    groupLabel: 'Site',
    ordinalPosition: 5,
    tooltip: 'Site activity measured by the pixel',
    filtersInGroup: [
      { name: 'site_events_filter', index: 1 },
      { name: 'site_pageviews_filter', index: 2 },
      { name: 'site_param1_filter', index: 3 },
      { name: 'site_param2_filter', index: 4 },
      { name: 'site_param3_filter', index: 5 },
      { name: 'site_param4_filter', index: 6 },
      { name: 'site_param5_filter', index: 7 },
      { name: 'site_formtext_filter', index: 8 },
      { name: 'site_clicktext_filter', index: 9 },
      { name: 'site_page_url_filter', index: 10 },
    ],
    icon: 'Os',
    customComponent: <Site />,
  },
  {
    groupLabelId: 'search_subgroup',
    parentGroupLabel: 'Engagement',
    groupLabel: 'Search',
    ordinalPosition: 6,
    tooltip: 'Search activity',
    filtersInGroup: [
      { name: 'search_events_filter', index: 1 },
      { name: 'search_collection_filter', index: 2 },
      { name: 'search_ad_group_filter', index: 3 },
      { name: 'search_keyword_filter', index: 4 },
      { name: 'search_campaign_filter', index: 5 },
    ],
    icon: 'DomainsTarget',
    customComponent: <Search />,
  },
  {
    groupLabelId: 'email_subgroup',
    parentGroupLabel: 'Engagement',
    groupLabel: 'Email',
    ordinalPosition: 7,
    tooltip: 'Email activity',
    filtersInGroup: [
      { name: 'email_events_filter', index: 1 },
      { name: 'email_collection_filter', index: 2 },
      { name: 'email_ad_group_filter', index: 3 },
      { name: 'email_campaign_filter', index: 4 },
      { name: 'email_action_filter', index: 5 },
    ],
    icon: 'Email',
    customComponent: <Email />,
  },
  {
    groupLabelId: 'social_subgroup',
    parentGroupLabel: 'Engagement',
    groupLabel: 'Social',
    ordinalPosition: 8,
    tooltip: 'Social Platforms activity',
    filtersInGroup: [
      { name: 'social_events_filter', index: 3 },
      { name: 'social_campaign_filter', index: 4 },
    ],
    customComponent: <Social />,
    icon: 'Userdomains',
  },
  {
    groupLabelId: 'media_subgroup',
    friendlyName: 'Engagement on Media',
    parentGroupLabel: 'Engagement',
    groupLabel: 'Media',
    ordinalPosition: 9,
    tooltip: 'Interactions with media content',
    filtersInGroup: [
      { name: 'media_campaign_filter', index: 2 },
      { name: 'media_action_filter', index: 3 },
      { name: 'media_frequency_filter', index: 5 },
    ],
    icon: 'Videocompletionrate',
    customComponent: <Media />,
  },
  {
    groupLabelId: 'affiliation_graph_subgroup',
    parentGroupLabel: 'Audience extensions',
    groupLabel: 'Affiliation Graph',
    ordinalPosition: 10,
    tooltip:
      'By selecting "Extend with Facility Affiliation" users can include healthcare professionals working within the same facility as those in the base population.',
    filtersInGroup: [{ name: 'extend_with_facility_affiliation', index: 1 }],
  },
  {
    groupLabelId: 'care_team_subgroup',
    parentGroupLabel: 'Audience extensions',
    groupLabel: 'Care Team',
    ordinalPosition: 11,
    tooltip:
      'Users can broaden the audience by including healthcare professionals who are part of the selected care team, with options to specify the type of care team construction.',
    filtersInGroup: [
      { name: 'extend_with_care_team', index: 1 },
      { name: 'care_team_type', index: 2 },
    ],
  },
];
